import { Divider } from 'antd';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getBoxRentalPriceAmount } from 'entities/Box';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { getSelectedBoxes } from '../../model/selectors/getSelectedBoxes';
import { getSelectedEmail } from '../../model/selectors/getSelectedEmail';
import { getSelectedWarehouse } from '../../model/selectors/getSelectedWarehouse';
import { SendOfferRentOptionData, useSendOfferMutation } from 'entities/Lead';
import { showNotification } from 'app/providers/NotificationsProvider';
import { getSelectedLanguage } from '../../model/selectors/getSelectedLanguage';
import { AppLanguage } from 'app/config/i18Config/types';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { TextArea } from 'shared/ui/TextArea';
import { sendOfferModalActions } from '../../model/slice/sendOfferModalSlice';
import { getOfferNote } from '../../model/selectors/getOfferNote';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface SummaryStepProps {
  onClose: () => void;
}

export const SummaryStep: FC<SummaryStepProps> = memo((props) => {
  const { onClose } = props;

  const { t } = useAppTranslation(['contracts', 'common']);

  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedEmail = useAppSelector(getSelectedEmail);
  const selectedUnits = useAppSelector(getSelectedBoxes);
  const note = useAppSelector(getOfferNote);
  const language = useAppSelector(getSelectedLanguage);

  const [sendOffer, { isLoading }] = useSendOfferMutation();

  const offerDetailsBullets = useMemo(
    () => [
      { label: t('Email'), value: selectedEmail },
      {
        label: t('Warehouse'),
        value: `${selectedWarehouse?.name}`,
      },
    ],
    [selectedEmail, selectedWarehouse?.name, t],
  );

  const offerBoxesBullets = useMemo(() => {
    const boxesBullets = selectedUnits.map((box) => {
      return {
        label: `${box.name} (${formatDecimal(box.sizeCode.square)} ${getSizeCodeTypeString(box.sizeCode.sizeCodeType)})`,
        value: `${formatPrice(getBoxRentalPriceAmount(box))} ${currencySymbol}`,
        emptyValueSymbol: `- ${currencySymbol}`,
      };
    });

    return boxesBullets;
  }, [currencySymbol, selectedUnits]);

  const rentOptionData: SendOfferRentOptionData[] = useMemo(() => {
    const rentOptionsMap = new Map<string, SendOfferRentOptionData>();

    selectedUnits.forEach((unit) => {
      if (!unit.rentOptions || unit.rentOptions.length === 0) {
        return;
      }

      unit.rentOptions.forEach((rentOption) => {
        const rentOptionId = rentOption.rentOptionId;
        const label = getLocalizedString(rentOption.label, language);
        const description = getLocalizedString(rentOption.description, language);

        if (!rentOptionsMap.has(rentOptionId)) {
          rentOptionsMap.set(rentOptionId, {
            label,
            description,
            units: [unit.name],
          });
        } else {
          const entry = rentOptionsMap.get(rentOptionId)!;
          if (!entry.units.includes(unit.name)) {
            entry.units.push(unit.name);
          }
        }
      });
    });

    return Array.from(rentOptionsMap.values());
  }, [language, selectedUnits]);

  const offerRentOptionsBullets = useMemo(() => {
    return rentOptionData.map((entry) => {
      const unitsList = entry.units.join(', ');

      return {
        label: (
          <div>
            <div>{entry.label}</div>
            <div className="text-primary text-sm">({unitsList})</div>
          </div>
        ),
        value: entry.description,
      };
    });
  }, [rentOptionData]);

  const handleSendOffer = useCallback(async () => {
    try {
      if (selectedEmail && selectedWarehouse) {
        const boxId = selectedUnits.map((box) => box.boxId);

        const sendOfferData = {
          email: selectedEmail,
          warehouseId: selectedWarehouse?.warehouseId,
          boxId,
          note,
          language: language || AppLanguage.ru,
          rentOptionData,
        };

        await sendOffer(sendOfferData).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Offer has been successfully sent'));

        onClose();
      }
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when sending offer'));
    }
  }, [selectedEmail, selectedWarehouse, selectedUnits, note, language, rentOptionData, sendOffer, t, onClose]);

  const noteChange = useCallback(
    (note: string) => {
      dispatch(sendOfferModalActions.setNote(note));
    },
    [dispatch],
  );

  return (
    <div className="flex flex-col">
      <div className="flex justify-between">
        <div className="flex flex-col pl-3">
          <div className="font-semibold text-2xl mb-4">{t('Offer details')}</div>
          <div className="border border-secondaryAccent rounded-lg py-7 px-3 w-[800px]">
            <BulletsTable heading={t('Details')} rows={offerDetailsBullets} theme="clear" />
            <Divider className="my-4" />
            <BulletsTable heading={t('Units')} rows={offerBoxesBullets} theme="clear" />

            <div>
              <Divider className="my-4" />
              {offerRentOptionsBullets.length > 0 ? (
                <BulletsTable heading={t('Rent options')} rows={offerRentOptionsBullets} theme="clear" />
              ) : (
                <div className="mb-3 text-accent font-semibold text-base">{t('No rent options for these sizes!')}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col w-[325px] pr-3">
          <div className="font-semibold text-2xl mb-4">{t('Note')}</div>
          <TextArea bordered placeholder={t('Add your note...')} className="!h-full" onChange={noteChange} rows={8} />
        </div>
      </div>

      <div className="flex self-end mt-8">
        <Button onClick={handleSendOffer} isLoading={isLoading}>
          {t('Send offer')}
        </Button>
      </div>
    </div>
  );
});
